import React, { useEffect, useState } from 'react';
import { aaiApi } from '../../services/aaiApi';
import type { MemberBoundary } from '../../types/aai';
import { Card, Chip, Divided, ErrorNote, Kicker, ListRow, Loading, Panel, RouteMark } from '../aa';

/*
 * The employee's read-only view of the organisation's boundary: Security and
 * data, and AI performance.
 *
 * Nothing here is editable by an employee, and the panels say who does manage
 * it rather than hiding the control. Where the platform genuinely does not
 * record a value yet (session length, retention window, data region), the row
 * says "Not recorded" instead of showing a confident default. A settings
 * screen that invents a retention period is worse than one that admits it does
 * not know: somebody will plan around the number.
 */

export type BoundaryPanel = 'security' | 'ai';

const NOT_RECORDED = 'Not recorded in the platform yet';

const valueOf = (value: string | null): string => {
  if (!value) return NOT_RECORDED;
  return value;
};

const chipFor = (value: string | null): React.ReactNode => {
  if (!value) return <Chip tone="neutral">Unset</Chip>;
  return <Chip tone="private">Managed</Chip>;
};

export const EmployeeBoundary: React.FC<{ panel: BoundaryPanel }> = ({ panel }) => {
  const [data, setData] = useState<MemberBoundary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    aaiApi.getMyPreferences()
      .then(({ data: payload }) => { if (active) setData(payload.boundary); })
      .catch(() => { if (active) setError('Could not load your policy boundary.'); });
    return () => { active = false; };
  }, []);

  if (error) return <ErrorNote message={error} />;
  if (!data) return <Loading label="Loading your boundary" />;

  if (panel === 'ai') {
    return (
      <div className="mx-auto max-w-[760px]">
        <Card className="mb-5">
          <Kicker>Current policy</Kicker>
          <div className="mt-1 flex items-center justify-between gap-3">
            <span className="text-[22px] font-bold tracking-[-0.01em] text-ink">{data.ai.policy}</span>
            <Chip tone="private">Protected</Chip>
          </div>
        </Card>
        <Panel>
          <Divided>
            <ListRow
              mark={<RouteMark kind="connection" size="sm" glyph="T" />}
              kicker="Cleared for use"
              title={`${data.ai.cleared_tools} evaluated tools`}
              foot="Published and announced vendor evaluations"
            />
            <ListRow
              mark={<RouteMark kind="connection" size="sm" glyph="R" />}
              kicker="Routing"
              title={valueOf(data.ai.routing)}
              foot="The approved pool is chosen per use case, not per person"
            />
            <ListRow
              mark={<RouteMark kind="connection" size="sm" glyph="E" />}
              kicker="Release"
              title={valueOf(data.ai.release)}
              foot="Evaluation, rollout and fallback"
            />
          </Divided>
        </Panel>
        <p className="mt-4 text-[12.5px] text-ink-muted">Policy managed by {data.managed_by}.</p>
      </div>
    );
  }

  const security = data.security;
  return (
    <div className="mx-auto max-w-[760px]">
      <Card className="mb-5">
        <Kicker>Current boundary</Kicker>
        <div className="mt-1 flex items-center justify-between gap-3">
          <span className="text-[22px] font-bold tracking-[-0.01em] text-ink">Your work</span>
          <Chip tone="ok">Policy active</Chip>
        </div>
      </Card>

      <Panel>
        <Divided>
          <ListRow
            mark={<RouteMark kind="connection" size="sm" glyph="S" />}
            kicker="Organisation managed"
            title="Your identity"
            foot={security.identity}
          />
          <ListRow
            mark={<RouteMark kind="quick_win" size="sm" />}
            kicker="Private"
            title="Quick Win runs"
            foot={security.run_privacy}
          />
          <ListRow
            mark={<RouteMark kind="connection" size="sm" glyph="A" />}
            kicker="Required"
            title="Source access"
            foot={security.source_access}
          />
        </Divided>
      </Panel>

      <Panel className="mt-5">
        <Divided>
          <ListRow
            kicker="Policy"
            title="Session length"
            foot={valueOf(security.session_policy)}
            right={chipFor(security.session_policy)}
          />
          <ListRow
            kicker="Policy"
            title="Run retention"
            foot={valueOf(security.run_retention)}
            right={chipFor(security.run_retention)}
          />
          <ListRow
            kicker="Policy"
            title="Data region"
            foot={valueOf(security.data_region)}
            right={chipFor(security.data_region)}
          />
          <ListRow
            kicker="Policy"
            title="External sharing"
            foot={valueOf(security.external_sharing)}
            right={chipFor(security.external_sharing)}
          />
        </Divided>
      </Panel>

      {data.policy.body && (
        <Card className="mt-5">
          <Kicker className="mb-2">Written policy</Kicker>
          <p className="whitespace-pre-line text-[13px] leading-[22.8px] text-ink-muted">{data.policy.body}</p>
        </Card>
      )}

      <p className="mt-4 text-[12.5px] text-ink-muted">Policy managed by {data.managed_by}.</p>
    </div>
  );
};

export default EmployeeBoundary;
